import { SentenceWithLink } from "./types"

export const getSentenceWithLinkPieces = (sentence: SentenceWithLink) => {
  const pieces = sentence.text.split(sentence.linkText)
  const [sentenceWithLinkPartOne, sentenceWithLinkPartTwo] = pieces || []
  return [sentenceWithLinkPartOne, sentence.linkText, sentenceWithLinkPartTwo]
}

export const formatDate = (date: Date | undefined, format: 'long' | 'numeric' = 'long') => {
  if (!date) return;

  if (format === 'numeric') {
    return date.toLocaleString("en-US", {
      timeZone: "America/New_York",
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const formatCurrency = (amount: number, locale: string = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export const formatPhoneNumber = (value: string) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Format the number based on length
  const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (!match) return '';

  let formatted = '';
  if (match[1]) {
    formatted += '(' + match[1];
  }
  if (match[1] && match[1].length === 3) {
    formatted += ') ';
  }
  if (match[2]) {
    formatted += match[2];
  }
  if (match[2] && match[2].length === 3) {
    formatted += '-';
  }
  if (match[3]) {
    formatted += match[3];
  }

  return formatted;
}

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent);
}

export const isTablet = () => {
  return isIpad() || /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(ip|ap|wp))))/.test(navigator.userAgent.toLowerCase());
}

const isIpad = () => {
  return (
    (/iPad/.test(navigator.userAgent)) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

export const getEarliestMoveInDate = () => {
  // Get current time in Eastern Time
  const now = new Date();
  const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));

  // Check if it's before 2 PM ET
  const currentHour = easternTime.getHours();
  const isBeforeTwoPM = currentHour < 14;

  let minDate;

  if (isBeforeTwoPM) {
    // Use today as minimum
    minDate = new Date();
  } else {
    // Check if tomorrow is Sunday (0 = Sunday)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (tomorrow.getDay() === 0) {
      // Tomorrow is Sunday, use today as minimum
      minDate = new Date();
    } else {
      // Tomorrow is not Sunday, use tomorrow as minimum
      minDate = tomorrow;
    }
  }

  // Convert to YYYY-MM-DD format for HTML date input
  const minDateString = minDate.toISOString().split('T')[0];
  return minDateString;
}

export const calculateProratedRent = (monthlyRent: number, currentDate = new Date()) => {
  // Get the current day of the month
  const currentDay = currentDate.getDate();

  // Get the total number of days in the current month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Calculate remaining days (including today)
  const remainingDays = daysInMonth - currentDay + 1;

  // Calculate pro-rated rent
  const dailyRate = monthlyRent / daysInMonth;
  const proratedRent = dailyRate * remainingDays;

  return Math.round(proratedRent * 100) / 100; // Round to 2 decimal places
}

// @TODO: CHange this to use products from the db so its easy to raise prices to addons
// `anchorDate` defaults to today (live reservations reserve+pay same day). The
// rescue/complete flow passes the customer's original reservation date so a
// customer paying later than they signed up is still pro-rated from when they
// reserved, not from the (later) completion day. See proration-anchor-policy.
export const calculateProratedTotal = (monthlyRent: number, need24hrAccess: boolean, need3xWeeklyAccess: boolean, anchorDate = new Date()): number => {
  return calculateProratedRent(monthlyRent, anchorDate) + (need24hrAccess ? calculateProratedRent(15, anchorDate) : 0) + (need3xWeeklyAccess ? calculateProratedRent(15, anchorDate) : 0);
}

export const calculateMonthlyRent = (baseRent: number, need24hrAccess: boolean, need3xWeeklyAccess: boolean): number => {
  return baseRent + (need24hrAccess ? 15 : 0) + (need3xWeeklyAccess ? 15 : 0);
}

export const getNextFirstOfMonth = (currentDate = new Date()): string => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get the first day of next month
  const nextMonth = new Date(year, month + 1, 1);

  return nextMonth.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}